import PageHero from "@/components/layout/PageHero";
import SEO from "@/components/layout/SEO";
import FeatureList from "@/components/layout/FeatureList";
import FinalCTA from "@/components/home/FinalCTA";
import { motion } from "framer-motion";
import {
  BookOpen,
  GraduationCap,
  Globe,
  ListChecks,
  ExternalLink,
  Trophy,
  Target,
  Sigma,
  Microscope,
  FlaskConical,
  ArrowUpRight,
} from "lucide-react";
import { ENTRY_EXAMS, IMG } from "@/lib/content";

const SUBJECT_ICONS = {
  math: Sigma,
  biology: Microscope,
  chemistry: FlaskConical,
};

const WHO = [
  {
    title: "Applicants from outside the OECD",
    body:
      "If your country is not part of the OECD, most Polish universities will require you to pass an entrance exam before admission — it's a standard step, not an exception.",
  },
  {
    title: "OECD applicants — often still required",
    body:
      "Coming from an OECD country does not automatically exempt you. Many universities still ask every candidate to sit the entrance exam, regardless of nationality.",
  },
  {
    title: "Competitive programs",
    body:
      "Medicine, Dentistry, Pharmacy and high-demand Bachelor / Master programs almost always run a written or computer-based entrance test for shortlisting.",
  },
  {
    title: "Why universities use it",
    body:
      "The entrance exam is how universities create a ranking of accepted applicants. Seats are limited; the test produces a defensible, comparable score across very different educational backgrounds.",
  },
];

export default function EntryExams() {
  return (
    <div data-testid="page-entry-exams">
      <SEO
        title="Polish university entrance exams — guide & prep courses"
        description="Who needs to take the entrance exam, why universities require it, and recommended subject-specific preparation courses for Math, Biology and Chemistry."
        path="/entry-exams"
      />
      <PageHero
        overline="Entry / Entrance Exams"
        title={
          <>
            Polish university entrance exams — <span className="italic text-[#D7141A]">what you actually need</span> to know.
          </>
        }
        subtitle="Most international applicants will sit an entrance exam — even those from OECD countries. Universities use it to rank candidates and to allocate limited seats fairly. Here's the practical view."
        image={IMG.krzAdvising}
      />

      {/* Who needs to take one */}
      <FeatureList
        overline="Who needs to take one"
        title="If you're applying internationally, plan for the exam."
        intro="The default assumption should be that you'll sit the entrance test. Many programs require it from everyone; the exceptions are rare and program-specific."
        items={WHO}
      />

      {/* Why the exam exists */}
      <section className="section-pad pt-0" data-testid="entry-exam-why">
        <div className="container-sp">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                icon: Trophy,
                overline: "Ranking system",
                title: "It creates a transparent ranking.",
                body: "Applicants come from very different education systems and grading scales. The entrance exam standardises everyone on the same scoring scale.",
              },
              {
                icon: Target,
                overline: "Limited seats",
                title: "It allocates limited seats.",
                body: "English-taught programs — especially Medicine, Dentistry and selective Bachelor / Master tracks — receive many more applications than they can admit.",
              },
              {
                icon: ListChecks,
                overline: "Program fit",
                title: "It confirms academic fit.",
                body: "The test checks that you actually have the foundation needed (sciences for medical programs, math for engineering, language for humanities).",
              },
            ].map((c, i) => (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.45, delay: i * 0.05 }}
                className="card-sp"
                data-testid={`entry-exam-why-${i}`}
              >
                <div className="w-11 h-11 rounded-xl bg-[#0D1B2A] text-[#D7141A] flex items-center justify-center">
                  <c.icon className="w-5 h-5" />
                </div>
                <div className="overline mt-5">{c.overline}</div>
                <h3 className="font-serif text-xl text-[#0D1B2A] mt-2 leading-snug">
                  {c.title}
                </h3>
                <p className="text-sm text-[#4A5568] mt-3 leading-relaxed">{c.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Gumroad courses */}
      <section className="section-pad pt-0" data-testid="entry-exam-courses">
        <div className="container-sp">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden rounded-[2rem] border border-[#1B2A3D] bg-[#0D1B2A] text-white p-8 md:p-14 grid md:grid-cols-12 gap-10 items-center"
          >
            <div
              aria-hidden
              className="absolute -top-24 -left-24 w-96 h-96 rounded-full blur-3xl opacity-30"
              style={{ background: "radial-gradient(circle, #D7141A 0%, transparent 70%)" }}
            />

            <div className="md:col-span-7 relative">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-white/80 mb-6">
                <BookOpen className="w-3.5 h-3.5 text-[#E5444A]" />
                Preparation courses
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight">
                Refresh, review, and prepare — the right way.
              </h2>
              <p className="text-white/70 mt-5 leading-relaxed max-w-xl">
                If you want to review the syllabus, refresh foundational
                knowledge, or work through the points actually tested on Polish
                university entrance exams — these courses are designed for
                exactly that. Curated by people who know the system from the
                inside.
              </p>

              <div className="mt-7 space-y-3 text-sm text-white/85">
                <div className="flex items-start gap-2.5">
                  <GraduationCap className="w-4 h-4 text-[#E5444A] mt-0.5 shrink-0" />
                  <span>Syllabus-aligned material for Polish university entrance tests</span>
                </div>
                <div className="flex items-start gap-2.5">
                  <Globe className="w-4 h-4 text-[#E5444A] mt-0.5 shrink-0" />
                  <span>Self-paced, accessible from anywhere in the world</span>
                </div>
                <div className="flex items-start gap-2.5">
                  <ListChecks className="w-4 h-4 text-[#E5444A] mt-0.5 shrink-0" />
                  <span>Practical review of the points that actually come up on the exam</span>
                </div>
              </div>

              <div className="mt-10">
                <a
                  href={ENTRY_EXAMS.coursesUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-gold"
                  data-testid="entry-exam-courses-cta"
                >
                  Browse courses on Gumroad <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>

            <div className="md:col-span-5 relative">
              <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-7">
                <div className="overline" style={{ color: "#E5444A" }}>What you'll find</div>
                <ul className="mt-5 space-y-3 text-sm text-white/85 leading-relaxed">
                  <li>· Subject refreshers (Biology · Chemistry · Math · Physics)</li>
                  <li>· Sample questions in the style of real exams</li>
                  <li>· Walk-throughs of common test sections</li>
                  <li>· Strategy notes for international candidates</li>
                </ul>
                <div className="mt-7 pt-5 border-t border-white/10 text-xs text-white/60 leading-relaxed">
                  Need help choosing the right course for your program?
                  Message us on WhatsApp — we'll point you to what fits your
                  shortlist.
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Subject-specific course cards */}
      <section className="section-pad pt-0" data-testid="entry-exam-subjects">
        <div className="container-sp">
          <div className="max-w-3xl mb-12">
            <div className="overline">Pick the right subject</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#0D1B2A] mt-3 leading-tight">
              Subject-specific entry-exam prep.
            </h2>
            <p className="text-[#4A5568] mt-5 leading-relaxed max-w-xl">
              Each program tests different fundamentals. Pick the course that
              matches the track you're applying to.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {ENTRY_EXAMS.subjects.map((s, i) => {
              const Icon = SUBJECT_ICONS[s.key] || BookOpen;
              return (
                <motion.a
                  key={s.key}
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.45, delay: i * 0.06 }}
                  className="group card-sp flex flex-col h-full"
                  data-testid={`entry-exam-subject-${s.key}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 rounded-xl bg-[#0D1B2A] text-[#D7141A] flex items-center justify-center">
                      <Icon className="w-5 h-5" />
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-[#718096] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-[#0D1B2A]" />
                  </div>
                  <h3 className="font-serif text-2xl text-[#0D1B2A] mt-6 leading-snug">
                    {s.subject}
                  </h3>
                  <div className="overline mt-2">{s.forWhom}</div>
                  <p className="text-sm text-[#4A5568] mt-4 leading-relaxed flex-1">
                    {s.summary}
                  </p>
                  <span className="mt-7 inline-flex items-center gap-1.5 text-sm font-medium text-[#D7141A]">
                    Open course <ExternalLink className="w-3.5 h-3.5" />
                  </span>
                </motion.a>
              );
            })}
          </div>

          <div className="mt-10 text-center">
            <a
              href={ENTRY_EXAMS.coursesUrl}
              target="_blank"
              rel="noreferrer"
              className="btn-outline"
              data-testid="entry-exam-all-courses"
            >
              Browse all courses on Gumroad <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
